import { computed, inject, Injectable, signal } from '@angular/core';
import { LayoutService } from '@content-provider';
import { catchError, of, tap, type Observable } from 'rxjs';

import { FALLBACK_MONTHLY_STATEMENT_LAYOUT } from './fallback/default-monthly-statement-layout';
import { LayoutSource } from './layout-source';
import type {
  FooterBlock,
  GreetingBlock,
  HeroBlock,
  LayoutBlock,
  LayoutBlockView,
  MemberDealsBlock,
  MonthlyStatementLayout,
  UpsellBannerBlock,
  VisitSummaryBlock,
} from './types';

export interface Deal {
  title: string;
  description: string;
  source: string;
}
export interface SummaryInfo {
  title: string;
  value: number;
}
type DynamicBlock = Exclude<LayoutBlockView, { type: 'hero_banner' | 'footer_banner' }>;
type StaticBlock = Extract<LayoutBlockView, { type: 'hero_banner' | 'footer_banner' }>;

@Injectable({ providedIn: 'root' })
export class MonthlyStatementContentService extends LayoutSource {
  private readonly layoutService = inject(LayoutService);
  //Change this mock with Loyalty API data
  readonly deals = signal<Deal[]>([
    { title: 'Buy 1, Get 1 FREE', description: 'OH SNAP! Dilly Bites**', source: 'oh-snap' },
    { title: 'Earn 20$ off/gal', description: 'When you buy 3 Gatorade (30 oz)**', source: 'gatorade' },
  ]).asReadonly();
  //Change this mock with Loyalty API data
  readonly summaryInfos = signal<SummaryInfo[]>([
    { title: 'Visit Balance', value: 1 },
    { title: 'Rewards waiting', value: 5 },
    { title: 'Visits from a milestone', value: 9 },
  ]).asReadonly();
  //Change this mock with Loyalty API data
  readonly MOCK_VISITS_COUNTER = 5;
  readonly visitsCounter = signal(this.MOCK_VISITS_COUNTER).asReadonly();
  readonly layout = signal<MonthlyStatementLayout | null>(null);
  readonly layoutBlocks = computed<LayoutBlockView[]>(() =>
    (this.layout()?.layout ?? [])
      .map((block: LayoutBlock) => this.toBlockView(block))
      .filter((b): b is LayoutBlockView => b !== null),
  );
  readonly dynamicLayoutBlocks = computed<DynamicBlock[]>(() =>
    this.layoutBlocks().filter((b): b is DynamicBlock => b.type !== 'hero_banner' && b.type !== 'footer_banner'),
  );
  readonly staticLayoutBlocks = computed<StaticBlock[]>(() =>
    this.layoutBlocks().filter((b): b is StaticBlock => b.type === 'hero_banner' || b.type === 'footer_banner'),
  );

  getLayout(): Observable<MonthlyStatementLayout> {
    return this.layoutService
      .getContent<MonthlyStatementLayout>(
        this.layoutService.ms_config.baseUrl,
        this.layoutService.MONTHLY_STATEMENT_KEY,
      )
      .pipe(
        catchError(() => of(FALLBACK_MONTHLY_STATEMENT_LAYOUT)),
        tap((layout) => {
          this.layout.set(layout);
        }),
      );
  }

  private toBlockView(block: LayoutBlock): LayoutBlockView | null {
    if ('hero' in block) {
      const h: HeroBlock = block.hero;

      return {
        type: 'hero_banner',
        data: {
          logo: h.logo ?? { url: '' },
          title: h.title,
          image: h.hero_image ?? { url: '/assets/header-image.png' },
        },
      };
    }

    if ('greeting' in block) {
      const g: GreetingBlock = block.greeting;

      return {
        type: 'greeting',
        subtitleFallback: g.subtitle_fallback,
        nameFallback: g.name_fallback,
        greetingTemplate: g.greeting_template,
      };
    }

    if ('visit_summary' in block) {
      const v: VisitSummaryBlock = block.visit_summary;

      return {
        type: 'visit_summary',
        data: { label: v.stats.label, dataSource: v.stats.data_source, emptyLabel: v.stats.empty_label },
      };
    }

    if ('upsell_banner' in block) {
      const u: UpsellBannerBlock = block.upsell_banner;

      return {
        type: 'upsell_banner',
        data: {
          title: u.title,
          body: u.body,
          unmetRequirementsTitle: u.unmet_requirements_title,
          unmetRequirementsBody: u.unmet_requirements_body,
          promoBanner: {
            title: u.promo_banner.title,
            body: u.promo_banner.body,
            icon: u.promo_banner.icon?.url ?? '/assets/coin.png',
          },
          link: u.link.href,
        },
      };
    }

    if ('member_deals' in block) {
      const m: MemberDealsBlock = block.member_deals;

      return {
        type: 'member_deals',
        data: { title: m.title, subtitle: m.subtitle, link: m.link.href, disclaimers: m.disclaimers },
      };
    }

    if ('footer' in block) {
      const f: FooterBlock = block.footer;

      return {
        type: 'footer_banner',
        data: {
          logo: f.logo ?? { url: '/assets/circlek-logo.png' },
          followLabel: f.follow_label,
          socialLinks: f.social_links,
          legalCopy: f.legal_copy,
          copyright: f.copyright,
        },
      };
    }

    return null;
  }
}
